import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, AlertTriangle, Shield } from 'lucide-react';
import { Header } from '../components/common/Header';
import { Footer } from '../components/common/Footer';
import { supabase } from '../utils/supabase';
import { useAuthStore } from '../stores/authStore';
import { useAiCreditsStore } from '../stores/aiCreditsStore';

export function StripeSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sessionData, setSessionData] = useState<any>(null);
  const [isSecurityError, setIsSecurityError] = useState(false);
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { loadCredits } = useAiCreditsStore();

  useEffect(() => {
    const checkSession = async () => {
      if (!sessionId) {
        setError('セッションIDが見つかりません。URLに session_id パラメータが含まれていることを確認してください。');
        setIsLoading(false);
        return;
      }

      // Verify user is authenticated before proceeding
      if (!user?.id) {
        setError('ユーザー認証が必要です。ログインしてから再度お試しください。');
        setIsLoading(false);
        return;
      }

      try {
        // Check session status in our database
        const { data, error } = await supabase
          .from('checkout_sessions')
          .select('*')
          .eq('id', sessionId)
          .single();

        if (error) {
          console.error('Error fetching session:', error);
          setError('セッション情報の取得に失敗しました');
          setIsLoading(false);
          return;
        }

        // CRITICAL SECURITY CHECK: Verify that the session belongs to the current user
        if (!data.user_id) {
          console.error('Session data missing user_id:', data);
          setError('セッションデータが不正です');
          setIsSecurityError(true);
          setIsLoading(false);
          return;
        }

        if (data.user_id !== user.id) {
          console.error('Session user mismatch:', {
            sessionUserId: data.user_id,
            currentUserId: user.id
          });
          setError('セッションが別のユーザーに属しています。セキュリティ上の理由により、ログアウトします。');
          setIsSecurityError(true);
          setIsLoading(false);
          
          // Force logout for security reasons
          setTimeout(() => {
            logout();
            navigate('/login', { replace: true });
          }, 3000);
          return;
        }

        setSessionData(data);
        
        // Refresh user data with latest information from users table
        try {
          
          // Get the latest user info from auth.users
          const { data: { user: latestUser }, error: userError } = await supabase.auth.getUser();
          if (userError || !latestUser) {
            console.error('Failed to get latest user info:', userError);
          } else {
            // Get the latest member data from users table (updated by webhook)
            const { data: memberData, error: memberError } = await supabase
              .from('users')
              .select('plan, role')
              .eq('id', user.id)
              .maybeSingle();
            
            if (memberError) {
              console.error('Failed to get latest member data:', memberError);
            } else if (memberData) {
              // Update the auth store with the latest information
              const updatedUser = {
                ...user,
                role: memberData.role || user.role || 'user',
                updatedAt: new Date().toISOString(),
                // Update plan information in user metadata if needed
                settings: {
                  ...user.settings,
                  currentPlan: memberData.plan
                }
              };
              
              useAuthStore.setState({
                user: updatedUser,
                isAuthenticated: true
              });
              
              console.log('User data refreshed with latest plan and role:', {
                plan: memberData.plan,
                role: memberData.role
              });
            }
          }
        } catch (refreshError) {
          console.error('Error refreshing user data:', refreshError);
          // Continue anyway - don't block the success page
        }
        
        // Only reload credits after successful security validation
        await loadCredits();
        
        // Log session data for debugging
        console.log('Session data retrieved:', data);
        
        setIsLoading(false);
      } catch (err) {
        console.error('Error checking session with ID:', sessionId, err);
        setError(`セッション情報の確認中にエラーが発生しました: ${err instanceof Error ? err.message : 'Unknown error'}`);
        setIsLoading(false);
      }
    };

    checkSession();
  }, [sessionId, user?.id, loadCredits, logout, navigate]);

  const handleContinue = () => {
    // Redirect based on session mode
    if (sessionData?.mode === 'subscription') {
      navigate('/account');
    } else {
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-3xl mx-auto px-4 py-16 min-h-[70vh]">
        <div className="bg-white rounded-lg shadow-sm p-8 text-center">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-12 h-12 text-blue-500 animate-spin mb-4" />
              <p className="text-gray-600">決済情報を確認中...</p>
            </div>
          ) : error ? (
            <div className="py-12">
              <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-6 ${
                isSecurityError ? 'bg-orange-100' : 'bg-red-100'
              }`}>
                {isSecurityError ? (
                  <Shield className="w-8 h-8 text-orange-600" />
                ) : (
                  <AlertTriangle className="w-8 h-8 text-red-600" />
                )}
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {isSecurityError ? 'セキュリティエラー' : 'エラーが発生しました'}
              </h2>
              <p className="text-gray-600 mb-8">{error}</p>
              {isSecurityError ? (
                <div className="space-y-4">
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
                    <p className="text-sm text-orange-700">
                      <strong>セキュリティ警告:</strong> 不正なセッションが検出されました。
                      安全のため、自動的にログアウトします。
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      logout();
                      navigate('/login', { replace: true });
                    }}
                    className="px-6 py-3 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors"
                  >
                    ログインページへ
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => navigate('/')}
                  className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  ホームに戻る
                </button>
              )}
            </div>
          ) : (
            <div className="py-12">
              <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-6">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">お支払いが完了しました</h2>
              <p className="text-gray-600 mb-8">
                {sessionData?.mode === 'subscription'
                  ? 'サブスクリプションの登録が完了しました。'
                  : 'AIクレジットの購入が完了しました。'}
              </p>
              <button
                onClick={handleContinue}
                className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                {sessionData?.mode === 'subscription' ? 'アカウント情報を確認' : 'ホームに戻る'}
              </button>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}